import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Download, FileText, Filter } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

interface ReportRow {
  id: string;
  driver_id: string | null;
  vehicle_id: string | null;
  period_month: string;
  total_waste_sar: string;
  total_idle_minutes: string;
  total_trips: number;
  total_km: string;
  composite_score_at_close: string | null;
  generated_at: string;
  driver_name: string | null;
  driver_code: string | null;
  vehicle_plate: string | null;
  vehicle_code: string | null;
}

interface Driver { id: string; fullNameEn: string | null; code: string | null }
interface Vehicle { id: string; plateEn: string | null; code: string | null }

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';
const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function previousMonth(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

export default function MonthlyReportsPage() {
  const [period, setPeriod] = useState<string>(previousMonth());
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [generatingDriver, setGeneratingDriver] = useState<string | null>(null);
  const [generatingVehicle, setGeneratingVehicle] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<ReportRow[]>('/api/v1/driver-coaching/reports/monthly?period=' + period);
      setRows(r.data ?? []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load drivers/vehicles for "generate now" lists
  useEffect(() => {
    apiClient.get('/api/v1/drivers?status=active&pageSize=200')
      .then((r) => {
        const data: unknown = r.data;
        if (Array.isArray(data)) setDrivers(data as Driver[]);
        else if (data && typeof data === 'object' && 'items' in data) {
          setDrivers(((data as { items?: Driver[] }).items) ?? []);
        }
      })
      .catch(() => setDrivers([]));
    apiClient.get('/api/v1/vehicles?status=active&pageSize=200')
      .then((r) => {
        const data: unknown = r.data;
        if (Array.isArray(data)) setVehicles(data as Vehicle[]);
        else if (data && typeof data === 'object' && 'items' in data) {
          setVehicles(((data as { items?: Vehicle[] }).items) ?? []);
        }
      })
      .catch(() => setVehicles([]));
  }, []);

  async function downloadPdfFor(row: ReportRow) {
    setDownloadingId(row.id);
    try {
      const url = row.driver_id
        ? '/api/v1/driver-coaching/reports/monthly/driver/pdf?driverId=' + row.driver_id + '&period=' + row.period_month.slice(0, 7)
        : '/api/v1/driver-coaching/reports/monthly/vehicle/pdf?vehicleId=' + row.vehicle_id + '&period=' + row.period_month.slice(0, 7);
      const filename = row.driver_id
        ? 'driver_' + (row.driver_code ?? 'driver') + '_' + row.period_month.slice(0, 7) + '.pdf'
        : 'vehicle_' + (row.vehicle_code ?? 'vehicle') + '_' + row.period_month.slice(0, 7) + '.pdf';
      await streamDownload(url, filename);
    } finally {
      setDownloadingId(null);
    }
  }

  async function generateDriverNow(driverId: string) {
    setGeneratingDriver(driverId);
    try {
      // Hitting the data endpoint upserts the cached row.
      await apiClient.get('/api/v1/driver-coaching/reports/monthly/driver?driverId=' + driverId + '&period=' + period);
      await refresh();
    } catch (err) {
      window.alert('Generate failed: ' + extractErrorMessage(err));
    } finally {
      setGeneratingDriver(null);
    }
  }

  async function generateVehicleNow(vehicleId: string) {
    setGeneratingVehicle(vehicleId);
    try {
      await apiClient.get('/api/v1/driver-coaching/reports/monthly/vehicle?vehicleId=' + vehicleId + '&period=' + period);
      await refresh();
    } catch (err) {
      window.alert('Generate failed: ' + extractErrorMessage(err));
    } finally {
      setGeneratingVehicle(null);
    }
  }

  const driverRows = rows.filter((r) => r.driver_id !== null);
  const vehicleRows = rows.filter((r) => r.vehicle_id !== null);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><FileText className="h-4 w-4" /> Monthly reports</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-border p-3 flex flex-wrap items-end gap-3">
          <Filter className="h-4 w-4 text-muted-foreground mt-2" />
          <div className="space-y-1">
            <Label htmlFor="period">Month</Label>
            <Input id="period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-44" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        ) : (
          <>
            <Section
              title="Driver reports"
              rows={driverRows.map((r) => ({
                id: r.id,
                label: r.driver_name ?? r.driver_code ?? '—',
                sub: 'Score ' + (r.composite_score_at_close !== null ? Number(r.composite_score_at_close).toFixed(0) : '—') +
                     ' · ' + r.total_trips + ' trips · ' + Math.round(Number(r.total_idle_minutes)) + ' min idle',
                cost: Number(r.total_waste_sar),
                onDownload: () => downloadPdfFor(r),
                downloading: downloadingId === r.id,
              }))}
              generators={drivers.map((d) => ({
                id: d.id,
                label: d.fullNameEn ?? d.code ?? d.id.slice(0, 8),
                onGenerate: () => generateDriverNow(d.id),
                generating: generatingDriver === d.id,
                covered: driverRows.some((r) => r.driver_id === d.id),
              }))}
            />

            <Section
              title="Vehicle reports"
              rows={vehicleRows.map((r) => ({
                id: r.id,
                label: r.vehicle_plate ?? r.vehicle_code ?? '—',
                sub: Math.round(Number(r.total_idle_minutes)) + ' wasted min',
                cost: Number(r.total_waste_sar),
                onDownload: () => downloadPdfFor(r),
                downloading: downloadingId === r.id,
              }))}
              generators={vehicles.map((v) => ({
                id: v.id,
                label: v.plateEn ?? v.code ?? v.id.slice(0, 8),
                onGenerate: () => generateVehicleNow(v.id),
                generating: generatingVehicle === v.id,
                covered: vehicleRows.some((r) => r.vehicle_id === v.id),
              }))}
            />
          </>
        )}
      </main>
    </div>
  );
}

interface SectionRow { id: string; label: string; sub: string; cost: number; onDownload: () => void; downloading: boolean }
interface SectionGenerator { id: string; label: string; onGenerate: () => void; generating: boolean; covered: boolean }

function Section({ title, rows, generators }: { title: string; rows: SectionRow[]; generators: SectionGenerator[] }) {
  const uncovered = generators.filter((g) => !g.covered);
  return (
    <div className="rounded-lg border border-border">
      <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
      {rows.length === 0 ? (
        <div className="p-4 text-xs text-muted-foreground">No reports for this month yet.</div>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{r.label}</div>
                <div className="text-xs text-muted-foreground">{r.sub}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm">{r.cost > 0 ? fmtSar(r.cost) : '—'}</span>
                <Button size="sm" variant="outline" className="gap-2" onClick={r.onDownload} disabled={r.downloading}>
                  {r.downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  PDF
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {uncovered.length > 0 && (
        <div className="px-4 py-2 bg-muted/20 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2">Generate missing reports for this month:</div>
          <div className="flex flex-wrap gap-2">
            {uncovered.map((g) => (
              <Button key={g.id} variant="outline" size="sm" onClick={g.onGenerate} disabled={g.generating} className="gap-2">
                {g.generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {g.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

async function streamDownload(path: string, filename: string): Promise<void> {
  const jwt = getStoredToken();
  const res = await fetch(API_BASE + path, {
    headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
  });
  if (!res.ok) {
    window.alert('PDF download failed: ' + res.statusText);
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
